import { Component, OnInit, OnDestroy, HostListener, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { AdminStudentPlatformHeaderComponent } from '../admin-student-platform-header/admin-student-platform-header.component';
import { AdminStudentPlatformService } from '@edtech-ui/core/services/admin-student-platform.service';
import { 
  DashboardOverviewData, 
  DashboardEntityItem, 
  MetricItem, 
  EntityInfo 
} from '@edtech-ui/core/models/admin-student-platform/admin-student-dashboard';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';

interface CardIconStyle {
  icon: string;
  iconBg: string;
}

const METRIC_ICON_MAP: Record<string, CardIconStyle> = {
  // Platform Overview
  'registered_users': { icon: 'group', iconBg: 'bg-purple' },
  'paid_subscriptions': { icon: 'verified', iconBg: 'bg-orange' },
  'colleges_onboarded': { icon: 'school', iconBg: 'bg-green' },
  'companies_onboarded': { icon: 'business', iconBg: 'bg-indigo' },
  'total_students': { icon: 'group', iconBg: 'bg-purple' },
  'total_subscriptions': { icon: 'verified', iconBg: 'bg-orange' },
  'total_colleges': { icon: 'school', iconBg: 'bg-green' },
  'total_companies': { icon: 'business', iconBg: 'bg-indigo' },

  // College Overview
  'students_registered': { icon: 'school', iconBg: 'bg-purple' },
  'paid_students': { icon: 'verified', iconBg: 'bg-orange' },
  'students_placed': { icon: 'how_to_reg', iconBg: 'bg-green' },
  'companies_hiring_here': { icon: 'business', iconBg: 'bg-indigo' },

  // Company Overview & Job Metrics
  'jobs_posted': { icon: 'work', iconBg: 'bg-purple' },
  'active_jobs': { icon: 'visibility', iconBg: 'bg-green' },
  'jobs_open_to_students': { icon: 'public', iconBg: 'bg-indigo' },
  'candidates_hired': { icon: 'how_to_reg', iconBg: 'bg-light-green' },
  'colleges_hired_from': { icon: 'school', iconBg: 'bg-sky' },

  // Learning & Practice
  'mock_assessments_taken': { icon: 'assignment', iconBg: 'bg-blue' },
  'mock_interviews_taken': { icon: 'record_voice_over', iconBg: 'bg-purple' },
  'quizzes_created': { icon: 'quiz', iconBg: 'bg-orange' },
  'courses_published': { icon: 'menu_book', iconBg: 'bg-green' },
  'resumes_created': { icon: 'description', iconBg: 'bg-sky' },

  // Hiring & Placement
  'assessments_conducted': { icon: 'assignment_turned_in', iconBg: 'bg-blue' },
  'company_assessments_taken': { icon: 'assignment_turned_in', iconBg: 'bg-blue' },
  'ai_interviews_conducted': { icon: 'smart_toy', iconBg: 'bg-sky' },
  'ai_interviews_taken': { icon: 'smart_toy', iconBg: 'bg-sky' },
  'total_jobs_posted': { icon: 'work_outline', iconBg: 'bg-indigo' },
  'active_jobs_open_to_students': { icon: 'visibility', iconBg: 'bg-green' },
  'candidates_placed': { icon: 'how_to_reg', iconBg: 'bg-green' },
};

@Component({
  selector: 'app-admin-student-platform-dashboard',
  standalone: true,
  imports: [
    AdminStudentPlatformHeaderComponent, 
    CommonModule, 
    HasPermissionDirective, 
    FormsModule
  ],
  templateUrl: './admin-student-platform-dashboard.component.html',
  styleUrl: './admin-student-platform-dashboard.component.scss'
})
export class AdminStudentPlatformDashboardComponent implements OnInit, OnDestroy {
  @ViewChild('entityDropdownRef', { static: false }) entityDropdownRef!: ElementRef;

  currentScope: 'platform' | 'college' | 'company' = 'platform';
  selectedEntityId: string | null = null;
  selectedEntity: DashboardEntityItem | null = null;
  selectedTimeRange: string = 'all_time';

  dashboardData: DashboardOverviewData | null = null;
  isLoading = true;
  hasError = false;

  // Dropdown & Search state
  isDropdownOpen = false;
  searchQuery = '';
  activeEntityTab: 'platform' | 'colleges' | 'companies' = 'platform';
  isSearchingEntities = false;
  collegesList: DashboardEntityItem[] = [];
  companiesList: DashboardEntityItem[] = [];

  private searchSubject = new Subject<string>();
  private subscription = new Subscription();

  timeRangeOptions = [
    { label: 'Today', value: 'today' },
    { label: '7 days', value: '7_days' },
    { label: '30 days', value: '30_days' },
    { label: '90 days', value: '90_days' },
    { label: 'All time', value: 'all_time' }
  ];

  PermissionForStudents = PermissionForStudents;

  constructor(
    private dashboardService: AdminStudentPlatformService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
    this.setupSearchSubscription();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isDropdownOpen && this.entityDropdownRef) {
      const clickedInside = this.entityDropdownRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.isDropdownOpen = false;
      }
    }
  }

  loadDashboardData(): void {
    this.isLoading = true;
    this.hasError = false;

    this.dashboardService.getDashboardOverview(
      this.currentScope,
      this.selectedEntityId || undefined,
      this.selectedTimeRange
    ).subscribe({
      next: (data) => {
        this.dashboardData = data;
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load dashboard overview stats:', err);
        this.isLoading = false;
        this.hasError = true;
      }
    });
  }

  toggleEntityDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
    if (this.isDropdownOpen) {
      this.searchQuery = '';
      if (this.currentScope === 'college') {
        this.activeEntityTab = 'colleges';
        this.fetchEntities('');
      } else if (this.currentScope === 'company') {
        this.activeEntityTab = 'companies';
        this.fetchEntities('');
      } else {
        this.activeEntityTab = 'platform';
      }
    }
  }

  setEntityTab(tab: 'platform' | 'colleges' | 'companies'): void {
    this.activeEntityTab = tab;
    this.searchQuery = '';

    if (tab === 'platform') {
      this.selectScope('platform');
      return;
    }

    this.fetchEntities('');
  }

  onSearchChange(query: string): void {
    this.searchSubject.next(query);
  }

  clearSearch(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.searchQuery = '';
    this.fetchEntities('');
  }

  private setupSearchSubscription(): void {
    this.subscription.add(
      this.searchSubject.pipe(
        debounceTime(250),
        distinctUntilChanged()
      ).subscribe((query) => {
        this.fetchEntities(query);
      })
    );
  }

  fetchEntities(search: string = ''): void {
    if (this.activeEntityTab === 'colleges') {
      this.isSearchingEntities = true;
      this.dashboardService.searchDashboardEntities('college', search, 1, 100).subscribe({
        next: (res) => {
          this.collegesList = res?.items || [];
          this.isSearchingEntities = false;
        },
        error: () => {
          this.isSearchingEntities = false;
        }
      });
    } else if (this.activeEntityTab === 'companies') {
      this.isSearchingEntities = true;
      this.dashboardService.searchDashboardEntities('company', search, 1, 100).subscribe({
        next: (res) => {
          this.companiesList = res?.items || [];
          this.isSearchingEntities = false;
        },
        error: () => {
          this.isSearchingEntities = false;
        }
      });
    }
  }

  getSearchPlaceholder(): string {
    if (this.activeEntityTab === 'colleges') {
      return 'Search college...';
    }
    if (this.activeEntityTab === 'companies') {
      return 'Search company...';
    }
    return 'Search...';
  }

  selectScope(scope: 'platform'): void {
    this.resetToPlatform();
    this.isDropdownOpen = false;
  }

  selectCollege(col: DashboardEntityItem): void {
    this.currentScope = 'college';
    this.selectedEntityId = col.id;
    this.selectedEntity = col;
    this.isDropdownOpen = false;
    this.loadDashboardData();
  }

  selectCompany(comp: DashboardEntityItem): void {
    this.currentScope = 'company';
    this.selectedEntityId = comp.id;
    this.selectedEntity = comp;
    this.isDropdownOpen = false;
    this.loadDashboardData();
  }

  resetToPlatform(): void {
    this.currentScope = 'platform';
    this.selectedEntityId = null;
    this.selectedEntity = null;
    this.activeEntityTab = 'platform';
    this.loadDashboardData();
  }

  onTimeRangeSelect(range: string): void {
    if (this.selectedTimeRange !== range) {
      this.selectedTimeRange = range;
      this.loadDashboardData();
    }
  }

  getHeaderTitle(): string {
    switch (this.currentScope) {
      case 'college':
        return 'College overview';
      case 'company':
        return 'Company overview';
      default:
        return 'Platform overview';
    }
  }

  getScopeIcon(): string {
    switch (this.currentScope) {
      case 'college':
        return 'school';
      case 'company':
        return 'business';
      default:
        return 'public';
    }
  }

  getScopeButtonText(): string {
    if (this.currentScope === 'college') {
      const name = this.dashboardData?.entity_info?.name || this.selectedEntity?.name;
      return name ? `Viewing: College - ${name}` : 'Viewing: College';
    }
    if (this.currentScope === 'company') {
      const name = this.dashboardData?.entity_info?.name || this.selectedEntity?.name;
      return name ? `Viewing: Company - ${name}` : 'Viewing: Company';
    }
    return 'Viewing: Entire platform';
  }

  getScopeButtonTooltip(): string {
    if (this.currentScope === 'college') {
      const name = this.dashboardData?.entity_info?.name || this.selectedEntity?.name;
      return name ? `Viewing College: ${name}` : 'Viewing College';
    }
    if (this.currentScope === 'company') {
      const name = this.dashboardData?.entity_info?.name || this.selectedEntity?.name;
      return name ? `Viewing Company: ${name}` : 'Viewing Company';
    }
    return 'Viewing: Entire platform';
  }

  getCardIconStyle(key: string): CardIconStyle {
    return METRIC_ICON_MAP[key] || { icon: 'analytics', iconBg: 'bg-purple' };
  }
}
